import React, { useState } from 'react';
import { MapPin, Phone, Mail, Instagram, Facebook, Youtube, ArrowRight } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { HouseLogo, Cloud, Star, Bird } from './Decor';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';

const Footer = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', parent: '', message: '' });
  const phoneNumber = "6289682201008"; 
  const { data: profile } = useQuery({ queryKey: ['public-school-profile'], queryFn: () => api.get('/school-profile').then((r) => r.data), retry: false });

const onSubmit = (e) => {
  e.preventDefault();

  if (!form.name || !form.parent) {
    toast({
      title: "Lengkapi data",
      description: "Nama anak dan orang tua wajib diisi.",
    });
    return;
  }

  const message = `
  *PENDAFTARAN TK MELATI ASRI*

  Halo Admin,

  Ada calon peserta didik yang menghubungi.

  --------------------------------

  Nama Anak
  : ${form.name}

  Nama Orang Tua
  : ${form.parent}

  Pesan
  : ${form.message || "-"}

  --------------------------------

  Mohon informasi lebih lanjut.

  Terima kasih.
  `;

  const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  window.open(url, "_blank");

  toast({
    title: "Mengalihkan ke WhatsApp",
    description: "Silakan tekan tombol kirim di WhatsApp.",
  });

  setForm({
    name: "",
    parent: "",
    message: "",
  });
};

  return (
    <footer id="kontak" className="relative overflow-hidden text-white" style={{ background: 'linear-gradient(180deg, #256d78 0%, #1a4f57 100%)' }}>
      {/* Sky top strip */}
      <div className="relative bg-sky-soft py-16 border-t-2 border-dashed border-[#c8dfe8]">
        <div className="absolute inset-0 pointer-events-none">
          <Cloud size={220} className="absolute top-4 left-[-30px] animate-cloud" />
          <Cloud size={180} className="absolute bottom-2 right-[-20px] animate-cloud-rev" />
          <Star size={26} className="absolute top-8 right-[30%] animate-twinkle" />
          <div className="absolute top-8 left-[30%] animate-bird"><Bird size={54} /></div>
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
          <h3 className="font-brush text-[42px] md:text-[56px] text-[#2b8b98] leading-[1]">Ayo Bergabung dengan Keluarga Melati!</h3>
          <p className="mt-3 font-hand text-[18px] text-[#4a3b2a]/85 max-w-[620px] mx-auto">
            Kirim pesan atau ajak si kecil untuk trial class gratis di TK Melati Asri.
          </p>
        </div>
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-2 gap-14">
        {/* Info */}
        <div>
          <div className="flex items-center gap-3">
            <HouseLogo size={52} />
            <div>
              <div className="font-hand text-[13px] tracking-widest text-[#a8dcc6]">TAMAN KANAK-KANAK</div>
              <div className="font-brush text-[28px]">MELATI ASRI</div>
            </div>
          </div>
          <p className="mt-5 font-hand text-[16px] text-white/85 max-w-[440px] leading-relaxed">
            Tempat anak-anak tumbuh dalam kasih sayang, seperti bunga melati yang harum
            dan menenangkan hati.
          </p>

          <div className="mt-8 space-y-4 font-hand text-[15px]">
            <div className="flex items-start gap-3">
              <MapPin size={20} className="mt-0.5 text-[#ffd4a1]" />
              <span>{profile?.address || 'Jl. Melati Indah No. 12, Kelurahan Asri, Jakarta Selatan 12345'}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={20} className="text-[#ffd4a1]" />
              <span>{profile?.phone || '+62 812-3456-7890 • (021) 555-1234'}</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail size={20} className="text-[#ffd4a1]" />
              <span>{profile?.email || 'halo@tkmelatiasri.sch.id'}</span>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            {[Instagram, Facebook, Youtube].map((Icon, i) => (
              <a
                key={i}
                href="#"
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#ee7a3d] flex items-center justify-center transition-colors"
                aria-label="social"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>

          <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 font-hand text-[14px]">
            <span className="w-2 h-2 rounded-full bg-[#7ed957]" />
            {profile?.opening_hours || 'Buka Senin - Jumat • 07.30 - 12.00 WIB'}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white text-[#3a2a1a] rounded-[28px] p-8 shadow-[0_12px_0_rgba(0,0,0,0.15)]">
          <h4 className="font-brush text-[32px] text-[#c74a2a]">Kirim Pesan</h4>
          <p className="font-hand text-[15px] text-[#4a3b2a]/80">Isi data di bawah, kami akan segera menghubungi Anda.</p>

          <form className="mt-5 space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="font-hand text-[14px]">Nama Anak</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Contoh: Melati Ayu"
                className="mt-1 w-full px-4 py-3 rounded-2xl border-2 border-[#f1e6d2] focus:border-[#3aa9b7] focus:outline-none font-hand transition-colors"
              />
            </div>
            <div>
              <label className="font-hand text-[14px]">Nama Orang Tua</label>
              <input
                type="text"
                value={form.parent}
                onChange={(e) => setForm({ ...form, parent: e.target.value })}
                placeholder="Nama lengkap"
                className="mt-1 w-full px-4 py-3 rounded-2xl border-2 border-[#f1e6d2] focus:border-[#3aa9b7] focus:outline-none font-hand transition-colors"
              />
            </div>
            <div>
              <label className="font-hand text-[14px]">Pesan</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                placeholder="Tanya jadwal trial class, dll"
                className="mt-1 w-full px-4 py-3 rounded-2xl border-2 border-[#f1e6d2] focus:border-[#3aa9b7] focus:outline-none font-hand resize-none transition-colors"
              />
            </div>
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#ee7a3d] text-white font-hand text-[17px] shadow-[0_6px_0_#b04a1e] hover:translate-y-[2px] hover:shadow-[0_4px_0_#b04a1e] transition-all"
            >
              Kirim Pesan <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </div>

      <div className="relative border-t border-white/10 py-6 text-center font-hand text-[14px] text-white/70">
        © {new Date().getFullYear()} TK Melati Asri • Dibuat dengan kasih sayang untuk anak-anak Indonesia.
      </div>
    </footer>
  );
};

export default Footer;
